import type { EventData, SelectionData } from "../store/selectionStore";
import { getPerformanceAggregateColor, type PerformancePointColorOptions } from "./performancePointColor";

export type SelectionRangeColorResolver = (
  row: Record<string, unknown> | undefined,
  eventId: number
) => string | undefined;

export type SourceLookup = { source_id: number; source_name: string };

/** Source name from maneuver/aggregate row field variants. */
export function extractRowSourceName(row: Record<string, unknown> | undefined): string | undefined {
  if (!row) return undefined;
  const raw = row.source_name ?? row.sourceName ?? row.Source_name ?? row.source;
  if (typeof raw === "string" && String(raw).trim() !== "") return String(raw).trim();
  return undefined;
}

/** Source id from row or resolved via source list + source_name. */
export function extractRowSourceId(
  row: Record<string, unknown> | undefined,
  sources?: SourceLookup[]
): number | undefined {
  if (!row) return undefined;
  const direct = row.source_id ?? row.Source_id ?? row.sourceId ?? row.sourceID;
  if (direct != null && Number.isFinite(Number(direct))) return Number(direct);
  const name = extractRowSourceName(row);
  if (!name || !Array.isArray(sources) || sources.length === 0) return undefined;
  const match = sources.find(
    (s) => String(s.source_name).toLowerCase() === name.toLowerCase()
  );
  return match?.source_id;
}

function attachSourceMeta(
  range: EventData,
  row: Record<string, unknown> | undefined,
  sources?: SourceLookup[]
): EventData {
  const source_name = extractRowSourceName(row);
  const source_id = extractRowSourceId(row, sources);
  if (source_name) range.source_name = source_name;
  if (source_id != null) range.source_id = source_id;
  return range;
}

/** Extract a datetime string from a row with common field name variants. */
export function normalizeRowDatetime(row: Record<string, unknown> | undefined): string | undefined {
  if (!row) return undefined;
  const raw =
    row.start_time ??
    row.Start_time ??
    row.end_time ??
    row.End_time ??
    row.datetime ??
    row.Datetime ??
    row.DATETIME ??
    row.date ??
    row.Date;
  if (raw == null) return undefined;
  if (typeof raw === "string" && String(raw).trim() !== "") return String(raw).trim();
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toISOString();
  return undefined;
}

function normalizeRangeEndTime(row: Record<string, unknown>): string | undefined {
  const raw = row.end_time ?? row.End_time;
  if (raw == null) return undefined;
  if (typeof raw === "string" && String(raw).trim() !== "") return String(raw).trim();
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toISOString();
  return undefined;
}

function attachRangeColor(
  range: EventData,
  row: Record<string, unknown> | undefined,
  eventId: number,
  resolveColor?: SelectionRangeColorResolver
): EventData {
  const color = resolveColor?.(row, eventId);
  return color ? { ...range, color } : range;
}

/** Point-in-time of a row, as ms. Maneuver and straight-line rows carry a single datetime, not a span. */
function cutRowTimeMs(row: Record<string, unknown> | undefined): number | null {
  if (!row) return null;
  const raw = row.datetime ?? row.Datetime ?? row.DATETIME ?? row.date ?? row.Date;
  if (raw == null) return null;
  const t = raw instanceof Date ? raw.getTime() : new Date(String(raw).trim()).getTime();
  return Number.isFinite(t) ? t : null;
}

/** A cut entry that is a time span rather than an event reference. */
type CutTimeRange = { start: number; end: number };

function toCutTimeRange(item: Record<string, unknown>): CutTimeRange | null {
  const rawStart = item.start_time ?? item.Start_time;
  const rawEnd = item.end_time ?? item.End_time;
  if (rawStart == null || rawEnd == null) return null;
  const start = rawStart instanceof Date ? rawStart.getTime() : new Date(String(rawStart)).getTime();
  const end = rawEnd instanceof Date ? rawEnd.getTime() : new Date(String(rawEnd)).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return start <= end ? { start, end } : { start: end, end: start };
}

/**
 * Narrow rows (maneuvers or straight-line points) to an active cut.
 *
 * `cutEvents` holds two shapes and both have to work. Cutting a table/legend selection stores event
 * references (a bare id, or a range object carrying `event_id`). Cutting a **map or timeseries brush**
 * stores a pure time span with no `event_id` at all — matching an id against those yields nothing, and
 * the pages used to fall through to "show none", emptying the table even though the span covered rows.
 * Spans are therefore matched on the row's own timestamp.
 *
 * A row survives when its `event_id` is cut, or its time falls inside a cut span.
 */
export function applyCutToRows<T extends Record<string, unknown>>(
  rows: T[],
  cut: ReadonlyArray<number | Record<string, unknown>>
): T[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (!Array.isArray(cut) || cut.length === 0) return rows;

  const cutIds = new Set<number>();
  const cutRanges: CutTimeRange[] = [];

  for (const item of cut) {
    if (typeof item === "number") {
      if (Number.isFinite(item)) cutIds.add(item);
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const rawId = (item as { event_id?: unknown }).event_id;
    const id = Number(rawId);
    if (rawId != null && Number.isFinite(id)) {
      cutIds.add(id);
      continue;
    }
    // No event reference — treat it as the time span it is.
    const range = toCutTimeRange(item);
    if (range) cutRanges.push(range);
  }

  // Nothing usable in the cut: keep the long-standing "show none" behaviour rather than silently
  // ignoring a cut the user asked for.
  if (cutIds.size === 0 && cutRanges.length === 0) return [];

  return rows.filter((row) => {
    const id = Number(row.event_id);
    if (Number.isFinite(id) && cutIds.has(id)) return true;
    if (cutRanges.length === 0) return false;
    const t = cutRowTimeMs(row);
    if (t == null) return false;
    return cutRanges.some((r) => t >= r.start && t <= r.end);
  });
}

/** Resolve a maneuver row by event_id from filtered table and/or full maneuvers list. */
export function findManeuverRowByEventId(
  eventId: number,
  tableRows: Array<Record<string, unknown>>,
  maneuverRows?: Array<Record<string, unknown>>
): Record<string, unknown> | undefined {
  const fromTable = tableRows.find((r) => r?.event_id == eventId);
  if (fromTable) return fromTable;
  if (!Array.isArray(maneuverRows) || maneuverRows.length === 0) return undefined;
  return maneuverRows.find((r) => r?.event_id == eventId);
}

/** Expand point-in-time maneuver rows to a short span for map overlay matching. */
export function ensureManeuverOverlayTimeSpan(
  start_time: string,
  end_time: string,
  paddingMs = 15_000
): { start_time: string; end_time: string } {
  const startMs = new Date(start_time).getTime();
  const endMs = new Date(end_time).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return { start_time, end_time };
  }
  if (endMs - startMs > 1000) {
    return { start_time, end_time };
  }
  return buildManeuverHighlightTimeRange(start_time, paddingMs);
}

function normalizeRowStartTime(row: Record<string, unknown> | undefined): string | undefined {
  if (!row) return undefined;
  const raw =
    row.start_time ??
    row.Start_time ??
    row.datetime ??
    row.Datetime ??
    row.DATETIME ??
    row.date ??
    row.Date;
  if (raw == null) return undefined;
  if (typeof raw === "string" && String(raw).trim() !== "") return String(raw).trim();
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toISOString();
  return undefined;
}

/** Build map highlight ranges from aggregate/table rows keyed by event_id. */
export function buildSelectedRangesFromRows(
  eventIds: number[],
  rows: Array<Record<string, unknown>>,
  resolveColor?: SelectionRangeColorResolver,
  sources?: SourceLookup[]
): EventData[] {
  if (!Array.isArray(eventIds) || eventIds.length === 0 || !Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  const ranges: EventData[] = [];
  for (const eventId of eventIds) {
    const row = rows.find((r) => r?.event_id == eventId);
    const rawStart = normalizeRowStartTime(row);
    if (rawStart) {
      const rawEnd = normalizeRangeEndTime(row ?? {}) ?? rawStart;
      const { start_time, end_time } = ensureManeuverOverlayTimeSpan(rawStart, rawEnd);
      ranges.push(
        attachSourceMeta(
          attachRangeColor(
            { event_id: Number(eventId), start_time, end_time, type: "event" },
            row,
            eventId,
            resolveColor
          ),
          row,
          sources
        )
      );
    }
  }
  return ranges;
}

/** Build map highlight ranges from getEventTimes / selection() rows. */
export function buildSelectedRangesFromSelectionData(
  selectionData: SelectionData[],
  eventIds?: number[],
  resolveColor?: SelectionRangeColorResolver,
  aggregateRows?: Array<Record<string, unknown>>,
  sources?: SourceLookup[]
): EventData[] {
  if (!Array.isArray(selectionData) || selectionData.length === 0) {
    return [];
  }
  const idSet = eventIds && eventIds.length > 0 ? new Set(eventIds) : null;
  const ranges: EventData[] = [];
  for (const row of selectionData) {
    if (!row || typeof row !== "object" || Array.isArray(row)) continue;
    const record = row as Record<string, unknown>;
    const eventIdRaw = record.event_id;
    const event_id =
      typeof eventIdRaw === "number"
        ? eventIdRaw
        : typeof eventIdRaw === "string"
          ? Number(eventIdRaw)
          : NaN;
    if (!Number.isFinite(event_id)) continue;
    if (idSet && !idSet.has(event_id)) continue;

    const rawStart =
      normalizeRowStartTime(record) ??
      (typeof record.start_time === "string" ? record.start_time.trim() : undefined);
    if (!rawStart) continue;
    const rawEnd = normalizeRangeEndTime(record) ?? rawStart;
    const { start_time, end_time } = ensureManeuverOverlayTimeSpan(rawStart, rawEnd);
    const aggregateRow = aggregateRows?.find((r) => r?.event_id == event_id);
    ranges.push(
      attachSourceMeta(
        attachRangeColor(
          { event_id, start_time, end_time, type: "event" },
          aggregateRow ?? record,
          event_id,
          resolveColor
        ),
        aggregateRow ?? record,
        sources
      )
    );
  }
  return ranges;
}

export interface BuildSelectedRangesOptions {
  /** selectedEvents() — drives BOTH the output order and the palette index space. */
  eventIds: readonly number[];
  /** selection() rows from getEventTimes. Preferred time source, but it lags the click and may cover a subset. */
  selectionData?: SelectionData[] | null;
  /** Table + maneuver/aggregate rows keyed by event_id. Per-id time fallback, and the row handed to resolveColor. */
  lookupRows?: Array<Record<string, unknown>> | null;
  resolveColor?: SelectionRangeColorResolver;
  sources?: SourceLookup[];
  /** Padding for point-in-time rows (default 15s, matching ensureManeuverOverlayTimeSpan). */
  overlayPaddingMs?: number;
}

/** Index rows by numeric event_id; first row wins so the caller's precedence order is respected. */
function indexRowsByEventId(
  rows: ReadonlyArray<unknown> | null | undefined
): Map<number, Record<string, unknown>> {
  const byId = new Map<number, Record<string, unknown>>();
  if (!Array.isArray(rows)) return byId;
  for (const row of rows) {
    if (!row || typeof row !== "object" || Array.isArray(row)) continue;
    const record = row as Record<string, unknown>;
    const id = Number(record.event_id);
    if (!Number.isFinite(id) || byId.has(id)) continue;
    byId.set(id, record);
  }
  return byId;
}

/**
 * One range per selected event id, in selectedEvents() order, resolved per-id.
 *
 * Replaces the all-or-nothing pairing of buildSelectedRangesFromSelectionData + buildSelectedRangesFromRows:
 * when the async selection() rows cover only part of the selection, that pair returned a non-empty PARTIAL
 * list, which suppressed the row fallback entirely and (via the callers' signature dedup) could be written
 * once and never corrected — the newest maneuver silently never reached the map. Here a missing id falls back
 * to its lookup row independently, and only an id with no time anywhere is skipped.
 *
 * Keeping the output aligned with eventIds also makes the array index coincide with the palette index, so a
 * consumer that falls back to positional coloring lands on the same color as the table.
 */
export function buildSelectedRangesForEvents(options: BuildSelectedRangesOptions): EventData[] {
  const { eventIds, selectionData, lookupRows, resolveColor, sources, overlayPaddingMs } = options;
  if (!Array.isArray(eventIds) || eventIds.length === 0) return [];

  const timeById = indexRowsByEventId(selectionData as ReadonlyArray<unknown> | null | undefined);
  const metaById = indexRowsByEventId(lookupRows);
  if (timeById.size === 0 && metaById.size === 0) return [];

  const ranges: EventData[] = [];
  const seen = new Set<number>();
  for (const rawId of eventIds) {
    const event_id = Number(rawId);
    if (!Number.isFinite(event_id) || seen.has(event_id)) continue;

    const timeRow = timeById.get(event_id);
    const metaRow = metaById.get(event_id);

    // Prefer the server-resolved times; fall back to the table row for ids the fetch hasn't returned yet.
    const startRow = normalizeRowStartTime(timeRow) ? timeRow : metaRow;
    const rawStart = normalizeRowStartTime(startRow);
    if (!rawStart) continue;
    const rawEnd =
      normalizeRangeEndTime(startRow ?? {}) ??
      normalizeRangeEndTime((startRow === timeRow ? metaRow : timeRow) ?? {}) ??
      rawStart;

    const { start_time, end_time } = ensureManeuverOverlayTimeSpan(rawStart, rawEnd, overlayPaddingMs);
    // Same precedence as the old `aggregateRow ?? record`: the table/aggregate row carries the color dimensions.
    const colorRow = metaRow ?? timeRow;

    seen.add(event_id);
    ranges.push(
      attachSourceMeta(
        attachRangeColor({ event_id, start_time, end_time, type: "event" }, colorRow, event_id, resolveColor),
        colorRow,
        sources
      )
    );
  }
  return ranges;
}

/** Stable signature for comparing map highlight ranges before writing selectionStore. */
export function selectionRangesSignature(
  ranges: Array<{
    event_id?: number;
    source_id?: number;
    start_time?: string;
    end_time?: string;
    color?: string;
  }>
): string {
  if (!Array.isArray(ranges) || ranges.length === 0) return "0";
  return ranges
    .map(
      (r) =>
        `${r.event_id ?? ""}:${r.source_id ?? ""}:${r.start_time ?? ""}:${r.end_time ?? ""}:${r.color ?? ""}`
    )
    .sort()
    .join("|");
}

/**
 * Signature that also pins the selected id set.
 *
 * Producers dedup on the range signature before writing selectionStore. With a partial build that meant
 * "same ranges, larger selection" hashed identically and the write was swallowed, so a later correction
 * never landed. Including the ids makes any selection change observable.
 */
export function selectionRangesSignatureForEvents(
  eventIds: readonly number[],
  ranges: Parameters<typeof selectionRangesSignature>[0]
): string {
  const ids = Array.isArray(eventIds) ? eventIds.join(".") : "";
  return `${ids}#${selectionRangesSignature(ranges)}`;
}

/**
 * Maneuver rows often have a single datetime — expand to a short window for map overlay matching.
 * MultiTrackLayer must not filter tracks by these ranges (overlay only); ContinuousTrackRenderer needs span > 0.
 */
export function buildManeuverHighlightTimeRange(
  startTime: string,
  paddingMs = 90_000
): { start_time: string; end_time: string } {
  const trimmed = String(startTime).trim();
  if (!trimmed) {
    return { start_time: startTime, end_time: startTime };
  }
  const center = new Date(trimmed.includes("T") ? trimmed : trimmed.replace(/ /g, "T"));
  if (Number.isNaN(center.getTime())) {
    return { start_time: trimmed, end_time: trimmed };
  }
  const pad = Number.isFinite(paddingMs) && paddingMs > 0 ? paddingMs : 90_000;
  return {
    start_time: new Date(center.getTime() - pad).toISOString(),
    end_time: new Date(center.getTime() + pad).toISOString(),
  };
}

/** Color resolver using performance page legend groups (same as AdvancedScatter). */
export function createPerformanceRangeColorResolver(
  options: PerformancePointColorOptions
): SelectionRangeColorResolver {
  return (row, _eventId) => {
    if (!row) return undefined;
    return getPerformanceAggregateColor(row, options);
  };
}
